import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {clearSelectedBpRoutine, fetchUserBpsRoutine, toggleFavouriteRoutine } from 'features/bpList/ducks/actions'
import { getBpsCount, getPagesCount, getUserBps } from 'features/bpList/ducks/selectors'
import styled, { keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import Filters from 'features/filters/Filters'
import { getFiltersQuery } from 'utils/filters'
import { isNotNilOrEmpty } from 'utils/ramda'
import { not, values, equals, isEmpty } from 'ramda'
import Drawer from '@mui/material/Drawer'
import FilterListIcon from '@mui/icons-material/FilterList'
import EmptyBpList from 'features/bpList/Components/EmptyBpsListState'
import { selectIsFetchingBps } from 'ducks/loaderSelectors'
import Skeleton from '@mui/material/Skeleton'
import { clearBpFilesRoutine } from 'ducks/files/actions'
import { useNavigate } from 'react-router-dom'
import { formatDate } from 'utils/date'
import { toast } from 'react-toastify'
import { requestTaskManagementFeature } from 'services/BpService'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import DormantBpModal from 'components/atoms/DormantBpModal'
import { ROLE_LABELS } from 'utils/roles'

const defaultFilters = {
  status: 'active,inactive,dormant',
  owner: [],
  responsible_person: [],
  created_at: null,
  updated_at: null,
  building_types: []
}

const renderBuildingOwner = (bp) => (
  bp.buildingOwner
    ? <span title={bp.buildingOwner}>{bp.buildingOwner}</span>
    : <NotSet title='No building owner recorded yet. Add it from the building page.'>Not set</NotSet>
)

const BpList = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const bps = useSelector(getUserBps)
  const pagesCount = useSelector(getPagesCount)
  const totalFound = useSelector(getBpsCount)
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [filters, setFilters] = useState(defaultFilters)
  const [sortingOptions, setSortingOptions] = useState({ column: 'name', desc: false, page: 1 })
  const [dormantModalOpen, setDormantModalOpen] = useState(false)
  const [dormantBpId, setDormantBpId] = useState(null)
  const [dormantBpName, setDormantBpName] = useState(null)
  const [dormantPaymentPastDue, setDormantPaymentPastDue] = useState(false)
  const [dormantPaymentManageable, setDormantPaymentManageable] = useState(false)
  const [requestedIds, setRequestedIds] = useState(new Set())

  const handleRequestFeature = async (bp) => {
    if (requestedIds.has(bp.id)) return
    setRequestedIds(prev => new Set(prev).add(bp.id))
    try {
      const res = await requestTaskManagementFeature(bp.id)
      toast.success(res?.data?.data?.alreadyRequested
        ? 'Already requested — the team has your details and will be in touch.'
        : `Request sent — the team will be in touch about ${bp.name}.`)
    } catch (e) {
      setRequestedIds(prev => {
        const next = new Set(prev)
        next.delete(bp.id)
        return next
      })
      toast.error(e?.response?.data?.message || 'Could not send the request. Try again.')
    }
  }

  const isLoading = useSelector(selectIsFetchingBps)

  const prevRequest = useRef({ sortingOptions: null, filters: null })
  const initialLoadComplete = useRef(false)

  const filtersQuery = useMemo(() => getFiltersQuery(filters), [filters])

  useEffect(() => {
    const { page, desc, column } = sortingOptions
    const sortParam = desc ? `-${column}` : column

    const isDifferent =
      !prevRequest.current.sortingOptions ||
      !prevRequest.current.filters ||
      prevRequest.current.sortingOptions.page !== page ||
      prevRequest.current.sortingOptions.desc !== desc ||
      prevRequest.current.sortingOptions.column !== column ||
      prevRequest.current.filters !== filtersQuery

    if (isDifferent || !initialLoadComplete.current) {
      dispatch(fetchUserBpsRoutine({ sort: sortParam, filters: filtersQuery, page }))

      prevRequest.current = {
        sortingOptions: { ...sortingOptions },
        filters: filtersQuery
      }
      initialLoadComplete.current = true
    }
  }, [sortingOptions, filtersQuery, dispatch])

  useEffect(() => {
    dispatch(clearSelectedBpRoutine())
    dispatch(clearBpFilesRoutine())
  }, [dispatch])

  const hasMore = sortingOptions.page < pagesCount
  const loadMore = useCallback(() => {
    setSortingOptions((prev) => ({ ...prev, page: prev.page + 1 }))
  }, [])

  const handleSetFilters = useCallback((next) => {
    setFilters(next)
    setSortingOptions((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [])

  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const handleClearFilters = useCallback(() => handleSetFilters(defaultFilters), [handleSetFilters])

  const shouldRenderClearButton = useMemo(() => {
    return values(filters).some((e) => isNotNilOrEmpty(e)) && not(equals(filters, defaultFilters))
  }, [filters])

  const handleCardClick = (bp) => {
    if ((bp.status || '').toLowerCase() === 'dormant') {
      setDormantBpId(bp.id)
      setDormantBpName(bp.name)
      setDormantPaymentPastDue(Boolean(bp.subscriptionPaymentPastDue))
      setDormantPaymentManageable(Boolean(bp.subscriptionPaymentManageable))
      setDormantModalOpen(true)
      return
    }
    navigate(`/bp-list/${bp.id}`)
  }

  const handleToggleFavourite = (e, bp) => {
    e.stopPropagation()

    if ((bp.status || '').toLowerCase() === 'dormant') {
      setDormantBpId(bp.id)
      setDormantBpName(bp.name)
      setDormantPaymentPastDue(Boolean(bp.subscriptionPaymentPastDue))
      setDormantPaymentManageable(Boolean(bp.subscriptionPaymentManageable))
      setDormantModalOpen(true)
      return
    }

    const isFav = bp.is_favourite || bp.isFavourite

    dispatch(toggleFavouriteRoutine({
      id: bp.id,
      isFavourite: isFav
    }))
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { bg: '#e6f7ff', text: '#1890ff' }
      case 'inactive':
        return { bg: '#fff1f0', text: '#f5222d' }
      case 'dormant':
        return { bg: '#fffbe6', text: '#faad14' }
      default:
        return { bg: '#f5f5f5', text: '#8c8c8c' }
    }
  }

  const hasServerResults = !isEmpty(bps)

  const BpCard = ({ bp, index }) => {
    const statusStyle = getStatusColor(bp.status)
    const address = [bp.street, bp.city].filter(Boolean).join(', ') || '-'

    const isFav = bp.is_favourite || bp.isFavourite

    return (
      <UnifiedCard data-tour-id={index === 0 ? 'buildings.first-row' : undefined} onClick={() => handleCardClick(bp)}>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <FavIconWrapper onClick={(e) => handleToggleFavourite(e, bp)} $isActive={isFav}>
              {isFav ? <StarIcon /> : <StarBorderIcon />}
            </FavIconWrapper>
            <CardTitle>{bp.name}</CardTitle>
          </div>

          {bp.status && (
            <StatusBadgeMobile bg={statusStyle.bg} text={statusStyle.text}>
              {bp.status}
            </StatusBadgeMobile>
          )}
        </CardHeader>

        <CardBody>
          <InfoGrid>
            <InfoItem>
              <Label>Tasks &amp; Remedials</Label>
              <Value onClick={e => e.stopPropagation()}>
                {bp.hasTaskManagement ? (
                  <FeatureOnChip title='Tasks, Remedials & Incident Reporting is enabled'>On</FeatureOnChip>
                ) : bp.isOwner ? (
                  <RequestFeatureBtn
                    type='button'
                    disabled={requestedIds.has(bp.id)}
                    onClick={() => handleRequestFeature(bp)}
                    title='Ask for Tasks, Remedials & Incident Reporting to be enabled for this building'
                  >
                    {requestedIds.has(bp.id) ? 'Requested ✓' : 'Request feature'}
                  </RequestFeatureBtn>
                ) : (
                  <OwnerOnlyNote title={`Tasks, Remedials & Incident Reporting isn't switched on for this building. Only an ${ROLE_LABELS.owner} of this building can request it.`}>{ROLE_LABELS.owner} can request</OwnerOnlyNote>
                )}
              </Value>
            </InfoItem>
            <InfoItem>
              <Label>Address</Label>
              <Value>{address}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Postcode</Label>
              <Value>{bp.postalCode || '-'}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Building owner</Label>
              <Value>{renderBuildingOwner(bp)}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Created</Label>
              <Value>{formatDate(bp.createdAt)}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Updated</Label>
              <Value>{formatDate(bp.updatedAt)}</Value>
            </InfoItem>
          </InfoGrid>
        </CardBody>
      </UnifiedCard>
    )
  }

  return (
    <PageWrapper>
      <ControlsRow>
        <ResultsInfo>
          {totalFound > 0 && hasServerResults && (
            <ResultsCount>
              <span style={{ fontWeight: 'bold' }}>{bps.length}</span>
              <span style={{ marginLeft: '5px' }}>of {totalFound} {t('bpList.countResults')}</span>
            </ResultsCount>
          )}
        </ResultsInfo>
        <ActionsGroup>
          {shouldRenderClearButton && <ClearButton onClick={handleClearFilters}>{t('bpList.clearFilters')}</ClearButton>}
          <FilterButton onClick={openDrawer}>
            <FiltersIcon />
            <FilterText>Filters</FilterText>
          </FilterButton>
        </ActionsGroup>
      </ControlsRow>

      <ContentArea>
        {isLoading && !hasServerResults ? (
          <>
            <MobileView>
              <CardsGrid>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i}>
                    <SkeletonCardHeader>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '6px', ml: 'auto' }} />
                    </SkeletonCardHeader>
                    <SkeletonCardBody>
                      {[...Array(6)].map((_, j) => (
                        <SkeletonRow key={j}>
                          <Skeleton variant="text" width={70} height={18} />
                          <Skeleton variant="text" width="45%" height={18} />
                        </SkeletonRow>
                      ))}
                    </SkeletonCardBody>
                  </SkeletonCard>
                ))}
              </CardsGrid>
            </MobileView>
            <DesktopView>
              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>Name</th>
                      <th>City</th>
                      <th>Post Code</th>
                      <th>Building owner</th>
                      <th>Status</th>
                      <th>Tasks &amp; Remedials</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(6)].map((_, i) => (
                      <tr key={i} style={{ cursor: 'default' }}>
                        <td><Skeleton variant="circular" width={20} height={20} /></td>
                        <td><Skeleton variant="text" width="80%" height={20} /></td>
                        <td><Skeleton variant="text" width="70%" height={20} /></td>
                        <td><Skeleton variant="text" width="60%" height={20} /></td>
                        <td><Skeleton variant="text" width="70%" height={20} /></td>
                        <td><Skeleton variant="text" width="65%" height={20} /></td>
                        <td><Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: '12px' }} /></td>
                        <td><Skeleton variant="text" width="60%" height={20} /></td>
                        <td></td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </TableContainer>
            </DesktopView>
          </>
        ) : !hasServerResults && equals(defaultFilters, filters) ? (
          <EmptyBpList />
        ) : !hasServerResults ? (
          <NoSearchResults>No Building Passports found.</NoSearchResults>
        ) : (
          <>
            <MobileView>
              <CardsGrid>
                {bps.map((bp, index) => (
                  <BpCard key={bp.id} bp={bp} index={index} />
                ))}
              </CardsGrid>
            </MobileView>
            <DesktopView>
              <TableContainer>
                <StyledTable>
                  <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th> {/* Coloana Stea */}
                    <th>Name</th>
                    <th>City</th>
                    <th>Post Code</th>
                    <th>Building owner</th>
                    <th>Status</th>
                    <th title='Tasks, Remedials & Incident Reporting'>Tasks &amp; Remedials</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                  </thead>
                  <tbody>
                  {bps.map((bp, index) => {
                    const statusColors = getStatusColor(bp.status)
                    const isFav = bp.is_favourite || bp.isFavourite

                    return (
                      <tr key={bp.id} data-tour-id={index === 0 ? 'buildings.first-row' : undefined} onClick={() => handleCardClick(bp)}>
                        <td onClick={(e) => handleToggleFavourite(e, bp)}>
                          <FavIconWrapper $isActive={isFav}>
                            {isFav ? <StarIcon /> : <StarBorderIcon />}
                          </FavIconWrapper>
                        </td>
                        <td>
                          <NameCellWrap>
                            <TableName>{bp.name}</TableName>
                          </NameCellWrap>
                        </td>
                        <td>{bp.city || '-'}</td>
                        <td>{bp.postalCode || '-'}</td>
                        <td>{renderBuildingOwner(bp)}</td>
                        <td>
                          {bp.status && (
                            <TableStatusBadge $bg={statusColors.bg} $text={statusColors.text}>
                              {bp.status}
                            </TableStatusBadge>
                          )}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {bp.hasTaskManagement ? (
                            <FeatureOnChip title='Tasks, Remedials & Incident Reporting is enabled'>On</FeatureOnChip>
                          ) : bp.isOwner ? (
                            <RequestFeatureBtn
                              type='button'
                              disabled={requestedIds.has(bp.id)}
                              title='Ask for Tasks, Remedials & Incident Reporting to be enabled for this Building Passport'
                              onClick={() => handleRequestFeature(bp)}
                            >
                              {requestedIds.has(bp.id) ? 'Requested ✓' : 'Request feature'}
                            </RequestFeatureBtn>
                          ) : (
                            <OwnerOnlyNote title={`Tasks, Remedials & Incident Reporting isn't switched on for this building. Only an ${ROLE_LABELS.owner} of this building can request it.`}>{ROLE_LABELS.owner} can request</OwnerOnlyNote>
                          )}
                        </td>
                        <td>{formatDate(bp.createdAt)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <ArrowIcon>→</ArrowIcon>
                        </td>
                      </tr>
                    )
                  })}
                  </tbody>
                </StyledTable>
              </TableContainer>
            </DesktopView>
          </>
        )}
        {hasServerResults && (
          <InfiniteScrollSentinel
            hasMore={hasMore}
            loading={isLoading}
            onLoadMore={loadMore}
            label="Loading more Building Passports…"
          />
        )}
      </ContentArea>

      <Drawer
        anchor='right'
        variant='persistent'
        open={isDrawerOpen}
        onClose={closeDrawer}
        style={{ zIndex: 1200 }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
            boxShadow: '-5px 0 25px rgba(0, 0, 0, 0.1)',
            width: '350px',
            maxWidth: '90vw'
          }
        }}
      >
        <FiltersWrapper>
          <FiltersHeader>
            <FiltersTitle>Filter Passports</FiltersTitle>
            <CloseButton onClick={closeDrawer}>×</CloseButton>
          </FiltersHeader>
          <FiltersContent>
            <Filters closeDrawer={closeDrawer} parentFilters={filters} getFilters={handleSetFilters} defaultFilters={defaultFilters} />
          </FiltersContent>
        </FiltersWrapper>
      </Drawer>
      <DormantBpModal
        open={dormantModalOpen}
        onClose={() => setDormantModalOpen(false)}
        bpId={dormantBpId}
        bpName={dormantBpName}
        paymentPastDue={dormantPaymentPastDue}
        canResolvePayment={dormantPaymentManageable}
      />
    </PageWrapper>
  )
}

export default BpList


const FavIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ $isActive }) => $isActive ? '#fadb14' : '#b0b0b0'};

  transition: transform 0.2s, color 0.2s;

  &:hover {
    transform: scale(1.1);
    color: ${({ $isActive }) => $isActive ? '#fadb14' : '#faad14'};
  }

  svg {
    font-size: 20px;
  }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  animation: ${fadeIn} 0.3s ease-out;
  box-sizing: border-box;
`

const MobileView = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: block;
  }
`

const DesktopView = styled.div`
  display: block;
  @media (max-width: 1024px) {
    display: none;
  }
`

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  overflow: hidden;
  width: 100%;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  thead {
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    th {
      padding: 16px 24px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: #6b7280;
      letter-spacing: 0.05em;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f3f4f6;
      transition: all 0.2s;
      cursor: pointer;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: #f9fafb;

        /* Highlight name on hover */
        td:first-child span {
          color: ${({ theme }) => theme.colors.primary};
        }
      }

      td {
        padding: 16px 24px;
        font-size: 14px;
        color: #111827;
        vertical-align: middle;
      }
    }
  }
`

const TableName = styled.span`
  font-weight: 600;
  color: #110c2f;
  transition: color 0.2s;
`

const NameCellWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const FeatureOnChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  color: #15803d;
  background: #dcfce7;
`

const RequestFeatureBtn = styled.button`
  font: inherit;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid rgba(12, 18, 41, 0.16);
  background: #fff;
  color: #0C1229;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: #f4f4f6; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const OwnerOnlyNote = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #8a909b;
  background: #f2f4f7;
  border: 1px solid #e4e7ec;
  white-space: nowrap;
  cursor: help;
`

const TableStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.$bg || '#f3f4f6'};
  color: ${props => props.$text || '#374151'};
`

const ArrowIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-size: 18px;
`

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`

const ResultsInfo = styled.div`
  display: flex;
  align-items: center;
`

const ResultsCount = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;
  background-color: #f5f7fa;
  padding: 8px 16px;
  border-radius: 8px;
`

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`

const ClearButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}30;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-2px);
  }
`

const FilterText = styled.span`
  margin-left: 8px;
  font-weight: 500;

  @media (max-width: 480px) {
    display: none;
  }
`

const FiltersIcon = styled(FilterListIcon)`
  font-size: 18px !important;
`

const ContentArea = styled.div`
  width: 100%;
`

const CardsGrid = styled.div`
  display: grid;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

 @media (max-width: 480px) {
   grid-template-columns: minmax(0, 1fr);
 }
`

const UnifiedCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    border-color: ${({ theme }) => theme.colors.primary}30;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f5f5f5;
  gap: 10px;
`

const CardTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.4;
`

const CardBody = styled.div`
  flex: 1;
`

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  min-width: 0;
`

const Label = styled.span`
  color: ${({ theme }) => '#111827'};
  font-weight: 500;
`

const Value = styled.span`
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
  text-align: right;
  min-width: 0;
  max-width: 65%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StatusBadgeMobile = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background-color: ${props => props.bg || '#36becd'};
  color: ${props => props.text || '#ffffff'};
  white-space: nowrap;
  letter-spacing: 0.5px;
`

const SkeletonCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
`

const SkeletonCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f5f5f5;
`

const SkeletonCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const NoSearchResults = styled.div`
  margin-top: 40px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 14px;
`

const FiltersContent = styled.div`
  padding: 0 20px;
`

const FiltersWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: white;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary}40;
    border-radius: 4px;
  }
`

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lighterGrey};
`

const FiltersTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lighterGrey};
  }
`

const NotSet = styled.span`
  color: #6b7280;
`
